// src/components/features/PathfinderModules/DailyStreak.js

import React, { useEffect, useMemo, useState } from 'react';
import { FaFire, FaExpandAlt, FaTimes, FaTrophy, FaCalendarCheck, FaChartLine } from 'react-icons/fa';
import { recordDailyActivity, fetchRecentDailyActivities } from '../../../api/dailyActivityApi';

const DailyStreak = ({
  expandedContainer,
  onExpand,
  onClose,
  isExpandedInCenter,
}) => {
  const isExpanded = expandedContainer === 'dailyStreak' || isExpandedInCenter;

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [stats, setStats] = useState({
    currentStreak: 0,
    bestStreak: 0,
    bestStreakEnd: null,
    totalActiveDays: 0,
    weeklySeries: [0, 0, 0, 0, 0, 0, 0],
    since: null
  });

  const dateKey = (d) => d.toISOString().slice(0, 10);

  const computeStats = (items) => {
    // items: [{date:'YYYY-MM-DD', count: number}]
    const byDate = new Map(items.map(i => [i.date, i.count || 0]));
    const dates = Array.from(byDate.keys()).sort(); // asc
    const today = new Date();
    const todayKey = dateKey(today);

    // current streak (consecutive days up to today)
    let current = 0;
    let cursor = new Date(today);
    while (true) {
      const k = dateKey(cursor);
      if (byDate.get(k) > 0) {
        current += 1;
        cursor.setDate(cursor.getDate() - 1);
      } else {
        break;
      }
    }

    // best streak (longest consecutive run in window)
    let best = 0;
    let bestEnd = null;
    // Build a boolean map for past N days window
    const windowDays = 365;
    const start = new Date(today);
    start.setDate(start.getDate() - (windowDays - 1));
    let run = 0;
    const scan = new Date(start);
    while (scan <= today) {
      const k = dateKey(scan);
      if (byDate.get(k) > 0) {
        run += 1;
        if (run > best) {
          best = run;
          bestEnd = new Date(scan);
        }
      } else {
        run = 0;
      }
      scan.setDate(scan.getDate() + 1);
    }

    // total active days
    const total = dates.filter(d => (byDate.get(d) || 0) > 0).length;

    // weekly series (last 7 days counts normalized 0..100)
    const last7 = [];
    const tmp = new Date(today);
    for (let i = 6; i >= 0; i--) {
      const d = new Date(today);
      d.setDate(today.getDate() - i);
      last7.push(byDate.get(dateKey(d)) || 0);
    }
    const max = Math.max(1, ...last7);
    const weeklySeries = last7.map(v => Math.round((v / max) * 100));

    const since = dates.length ? dates[0] : null;

    return {
      currentStreak: current,
      bestStreak: best,
      bestStreakEnd: bestEnd ? bestEnd.toISOString() : null,
      totalActiveDays: total,
      weeklySeries,
      since
    };
  };

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoading(true);
        setErr(null);
        // Count opening this module as activity (can be any interaction in the day)
        await recordDailyActivity('daily_streak_view', 1).catch(() => {});
        const items = await fetchRecentDailyActivities(365);
        if (!mounted) return;
        setStats(computeStats(items));
      } catch (e) {
        if (!mounted) return;
        setErr(e.message || 'Failed to load streak');
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  const progressWidth = useMemo(() => {
    if (stats.bestStreak <= 0) return '0%';
    return `${Math.min(100, Math.round((stats.currentStreak / stats.bestStreak) * 100))}%`;
  }, [stats.currentStreak, stats.bestStreak]);

  const bestAchievedText = useMemo(() => {
    if (!stats.bestStreakEnd) return '—';
    try {
      return new Date(stats.bestStreakEnd).toLocaleDateString();
    } catch {
      return '—';
    }
  }, [stats.bestStreakEnd]);

  const sinceText = useMemo(() => {
    if (!stats.since) return '—';
    try {
      return new Date(stats.since).toLocaleDateString();
    } catch {
      return '—';
    }
  }, [stats.since]);

  const renderCompactView = () => (
    <div className="streak-content">
      <h4>Current Streak</h4>
      {loading ? (
        <div className="loading">Loading…</div>
      ) : err ? (
        <div className="error">{err}</div>
      ) : (
        <div className="streak-display">
          <div className="streak-icon">
            <FaFire size={24} />
          </div>
          <div className="streak-number">{stats.currentStreak}</div>
          <div className="streak-label">days</div>
        </div>
      )}
    </div>
  );

  const renderExpandedView = () => (
    <div className="expanded-content">
      <h2>Your Progress Journey</h2>
      
      <div className="stats-grid">
        <div className="stat-card current">
          <FaFire className="stat-icon" />
          <div className="stat-info">
            <h3>Current Streak</h3>
            <div className="stat-value">{loading ? '—' : `${stats.currentStreak} Days`}</div>
            <div className="progress-bar">
              <div className="progress" style={{ width: progressWidth }}></div>
            </div>
            <p>{err ? '—' : 'Keep going! You\'re building momentum!'}</p>
          </div>
        </div>

        <div className="stat-card best">
          <FaTrophy className="stat-icon" />
          <div className="stat-info">
            <h3>Best Streak</h3>
            <div className="stat-value">{loading ? '—' : `${stats.bestStreak} Days`}</div>
            <p>Achieved on {loading ? '—' : bestAchievedText}</p>
          </div>
        </div>

        <div className="stat-card total">
          <FaCalendarCheck className="stat-icon" />
          <div className="stat-info">
            <h3>Total Active Days</h3>
            <div className="stat-value">{loading ? '—' : stats.totalActiveDays}</div>
            <p>Since {loading ? '—' : sinceText}</p>
          </div>
        </div>

        <div className="stat-card trend">
          <FaChartLine className="stat-icon" />
          <div className="stat-info">
            <h3>Weekly Progress</h3>
            <div className="mini-chart">
              {stats.weeklySeries.map((h, i) => (
                <div key={i} className="chart-bar" style={{ height: `${h}%` }}></div>
              ))}
            </div>
            <p>Last 7 days activity</p>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className={`info-box daily-streak ${isExpandedInCenter ? '' : ''}`}>
      {!isExpandedInCenter && (
        isExpanded ? (
          <button className="control-btn close" onClick={onClose} title="Close">
            <FaTimes size={12} />
          </button>
        ) : (
          <button className="control-btn expand" onClick={() => onExpand('dailyStreak')} title="Expand">
            <FaExpandAlt size={12} />
          </button>
        )
      )}

      {isExpandedInCenter ? renderExpandedView() : renderCompactView()}

      <style jsx>{`
        .info-box {
          background: #fff;
          border-radius: 16px;
          overflow: hidden;
        }

        .streak-content {
          padding: 1.5rem;
        }

        .streak-content h4 {
          font-size: 0.875rem;
          font-weight: 600;
          color: #64748b;
          margin: 0;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .expanded-content {
          padding: 0;
        }

        .expanded-content h2 {
          padding: 2.5rem;
          margin: 0;
          text-align: center;
          color: #1e293b;
          font-size: 1.875rem;
          font-weight: 700;
          letter-spacing: -0.025em;
          border-bottom: 1px solid #e2e8f0;
        }

        .stats-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 1.5rem;
          padding: 2.5rem;
        }

        @media (max-width: 768px) {
          .expanded-content h2 { padding: 1.5rem; font-size: 1.5rem; }
          .stats-grid { 
            grid-template-columns: 1fr;
            padding: 1.5rem;
            gap: 1rem;
          }
          .streak-number { font-size: 2rem; }
        }

        .stat-card {
          position: relative;
          background: #fff;
          border: 1px solid #e2e8f0;
          border-radius: 16px;
          padding: 1.5rem;
          transition: all 0.2s ease;
          overflow: hidden;
        }

        .stat-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 4px;
          background: #e2e8f0;
        }

        .stat-card.current::before {
          background: #f59e0b;
        }

        .stat-card.best::before {
          background: #8b5cf6;
        }

        .stat-card.total::before {
          background: #10b981;
        }

        .stat-card.trend::before {
          background: #3b82f6;
        }

        .stat-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 12px 24px -4px rgba(0, 0, 0, 0.08);
        }

        .stat-icon {
          font-size: 1.5rem;
          margin-bottom: 1rem;
        }

        .mini-chart {
          height: 80px;
          display: flex;
          align-items: flex-end;
          gap: 4px;
          padding: 1rem 0;
          border-bottom: 1px solid #e2e8f0;
        }

        .chart-bar {
          flex: 1;
          background: #3b82f6;
          border-radius: 4px 4px 0 0;
          transition: height 0.6s cubic-bezier(0.4, 0, 0.2, 1);
        }

        @keyframes flame {
          0%, 100% { transform: scale(1) rotate(-4deg); }
          50% { transform: scale(1.1) rotate(4deg); }
        }

        .progress-bar {
          width: 100%;
          height: 6px;
          background: #e2e8f0;
          border-radius: 3px;
          margin: 1rem 0;
          overflow: hidden;
        }

        .progress {
          height: 100%;
          background: #f59e0b;
          border-radius: 3px;
          transition: width 1s ease;
        }

        .streak-display {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 0.5rem;
        }

        .streak-icon {
          color: #f59e0b;
          animation: flame 2s ease-in-out infinite;
        }

        .streak-number {
          font-size: 2.5rem;
          font-weight: 700;
          color: #0f172a;
          line-height: 1;
        }

        .streak-label {
          font-size: 0.875rem;
          color: #64748b;
          font-weight: 500;
        }

        .streak-stats {
          display: flex;
          align-items: center;
          gap: 1.5rem;
          padding: 0.75rem 1.25rem;
          background: #f8fafc;
          border-radius: 8px;
          margin-top: 0.5rem;
        }

        .stat-item {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 0.25rem;
        }

        .stat-value {
          font-size: 1.125rem;
          font-weight: 600;
          color: #334155;
        }

        .stat-label {
          font-size: 0.75rem;
          color: #64748b;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .stat-divider {
          width: 1px;
          height: 24px;
          background: #e2e8f0;
        }

        .control-btn {
          position: absolute;
          top: 0.75rem;
          right: 0.75rem;
          padding: 0.5rem;
          border: none;
          background: transparent;
          color: #94a3b8;
          cursor: pointer;
          transition: all 0.2s ease;
          border-radius: 6px;
        }

        .control-btn:hover {
          background: #f1f5f9;
          color: #64748b;
        }

        .control-btn.close:hover {
          background: #fee2e2;
          color: #ef4444;
        }

        .loading { padding: 0.5rem; color: #64748b; }
        .error { padding: 0.5rem; color: #b91c1c; }
      `}</style>
    </div>
  );
};

export default DailyStreak;